import { IncentivizeRewardsDto } from '../../../../../../../../types/spool-local';
import moment from 'moment';
import { useContext } from 'react';
import Button from '../../../../../../../../components/Button';
import {
    INCENTIVES_REMOVED,
    INCENTIVES_REMOVE_PENDING,
    IncentivizeSpoolTypes,
} from '../../../../../../../../config/constants';
import appContext from '../../../../../../../../context/app/appContext';
import errorContext from '../../../../../../../../context/error/errorContext';
import modalContext from '../../../../../../../../context/modal/modalContext';
import {
    formatAccount,
    formatRemainingTime,
    formatUSD,
} from '../../../../../../../../helpers/formats';
import useSpoolsCreatedByUser from '../../../../../../../../hooks/web3/app/useSpoolsCreatedByUser';
import { sleep } from '../../../../../../../../web3/utils';

type Props = {
    incentivizedReward: IncentivizeRewardsDto;
    index: number;
};

const SpoolIncentive: React.FC<Props> = ({ incentivizedReward, index }) => {
    const {
        id,
        name,
        remainingAmount,
        totalAmount,
        emissionEndDate,
        totalEmissionDays,
        symbol,
    } = incentivizedReward;

    const { interfaceSDK, selectedSpool } = useContext(appContext);

    const { setError } = useContext(errorContext);

    const { openModal, closeModal, setModalData } = useContext(modalContext);

    const { updateOnIncentivize } = useSpoolsCreatedByUser();

    const timeRemaining = moment.unix(+emissionEndDate).diff(moment(), 'seconds');

    const handleClick = (type: IncentivizeSpoolTypes) => {
        openModal('incentivizeSpoolModal');
        setModalData((prev) => ({ ...prev, incentivizedReward, type }));
    };

    const handleRemove = async () => {
        if (!interfaceSDK || !selectedSpool) return;

        try {
            setModalData((prev) => ({ ...prev, message: INCENTIVES_REMOVE_PENDING }));
            openModal('waitingModal');

            const vaultContract = interfaceSDK.get.getVaultContractInstance(
                selectedSpool.address
            );

            const remove = await vaultContract.removeReward(id);

            const receipt = await remove.wait();

            await sleep(3000);

            await updateOnIncentivize(selectedSpool, 'REMOVE_INCENTIVE', id);

            setModalData((prev) => ({
                ...prev,
                status: INCENTIVES_REMOVED,
                message: '',
                txHash: receipt.transactionHash,
            }));
            openModal('successModal');
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <div className='current-incentive'>
            <div className='token-name'>
                <div className='title'>Incentive Token ({index + 1}/5)</div>
                <div className='value'>
                    <span className='bold'>{name}</span>
                </div>
            </div>
            <div className='remaining-tokens'>
                <div className='title'>Remaining Tokens to be Emitted</div>
                <div className='value'>
                    <span className='bold'>{formatUSD(remainingAmount)} </span>
                    out of
                    <span className='bold'>
                        {' '}
                        {formatUSD(totalAmount)} {symbol}
                    </span>
                </div>
            </div>
            <div className='token-address'>
                <div className='title'>Token Address</div>
                <div className='value'>{formatAccount(id)}</div>
            </div>

            <div className='days-left'>
                <div className='title'>Time Left Until Completion</div>
                <div className='value'>
                    <span className='bold'>
                        {moment.unix(+emissionEndDate).isAfter(moment())
                            ? formatRemainingTime(timeRemaining).timeAndUnit
                            : 0}{' '}
                    </span>
                    out of <span className='bold'>{totalEmissionDays}</span>
                </div>
            </div>
            <div className='buttons-container'>
                {moment.unix(+emissionEndDate).isBefore(moment()) ? (
                    <>
                        <Button
                            onClick={() =>
                                handleClick(IncentivizeSpoolTypes.REINCENTIVIZE)
                            }
                        >
                            Re-Incentivize
                        </Button>
                        <Button variant='secondary' onClick={handleRemove}>
                            Remove
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={() => handleClick(IncentivizeSpoolTypes.EXTEND)}>
                            Extend
                        </Button>
                        <Button
                            onClick={() => handleClick(IncentivizeSpoolTypes.INCREASE)}
                        >
                            Increase
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

export default SpoolIncentive;
